import { format, parseISO } from 'date-fns';
import { createStore } from 'devextreme-aspnet-data-nojquery';
import { Button } from 'devextreme-react/button';
import { CheckBox } from 'devextreme-react/check-box';
import DataGrid, { Column, Editing, Paging, RemoteOperations, Scrolling, StringLengthRule } from 'devextreme-react/data-grid';
import { Popup } from 'devextreme-react/popup';
import SelectBox from 'devextreme-react/select-box';
import { TextArea } from 'devextreme-react/text-area';
import { TextBox, Button as TextBoxButton } from 'devextreme-react/text-box';
import { Tooltip } from 'devextreme-react/tooltip';
import ValidationGroup from 'devextreme-react/validation-group';
import { RequiredRule, Validator } from 'devextreme-react/validator';
import DataSource from 'devextreme/data/data_source';
import React, { memo, useEffect, useId, useRef, useState } from 'react';
import grantApi from '../../api/GrantApi.js';
import sharedApi from '../../api/SharedApi.js';
import userApi from '../../api/UserApi.js';
import { GrantCommentAudienceType } from '../../dtos/GrantCommentAudienceType';
import { Modules } from '../../dtos/Modules.js';
import { useAppContext } from '../shared/AppContext';
import { DisplayType } from '../shared/Types.js';
import { getUserFullName, truncateWithEllipsis } from '../shared/Utils';
import GrantDetailCommentTooltip from './GrantDetailCommentTooltip';
import './GrantDetailComments.css';

interface IGrantDetailCommentsProps {
    grantId: string;
    phaseId?: string; // Optional, if you want to handle comments for a specific phase
    stepId?: string; // Optional, if you want to handle comments for a specific step
    name?: string;
    audienceOptions?: { text: string; value: number }[];
    defaultAudience?: GrantCommentAudienceType;
    // Roles that can read a comment saved with the audience above, named in its tooltip.
    audienceRoleNames?: string[];
    returnLastCommentDate?: (date: string | null) => void;
    displayType: DisplayType;
    onCommentAdded?: () => void; //TODO add additional event handlers and possibly pass comment data
    isReadOnly: boolean;
    module: Modules;
    isDisabled?: boolean;
    notifyDefault?: boolean;
    // When false, hide the "Notify all participants via email" checkbox.
    // Legacy phase-level comments (DC) always show it; non-legacy callers gate on whether
    // the OnComment notification is enabled in the resolved config.
    notifyVisible?: boolean;
}

export default memo(function GrantDetailComments({ grantId, phaseId, stepId, name, audienceOptions, returnLastCommentDate, displayType, onCommentAdded, isReadOnly, module, isDisabled = false, notifyDefault = true, notifyVisible = true, defaultAudience = GrantCommentAudienceType.All, audienceRoleNames = [] }: IGrantDetailCommentsProps) {

    const app = useAppContext();

    const audienceTooltipTargetId = `comment-audience-${useId().replace(/[:]/g, "")}`;
    const showAudience = !!audienceOptions && audienceOptions.length > 0;

    const audienceSelectBoxRef = useRef<SelectBox>(null);
    const commentTextBoxRef = useRef<TextBox>(null);
    const editingref = useRef(true);
    const grid = useRef<DataGrid>(null);
    const interval = useRef<NodeJS.Timeout | null>(null);
    const lastCommentTooltip = useRef(null);
    const notifyCheckRef = useRef<CheckBox>(null);
    const newCommentTextbox = useRef<TextArea>(null);
    const newCommentButton = useRef<Button>(null);
    const newCommentTextValidationGroup = useRef<ValidationGroup>(null);
    const parentStateHasBeenSet = useRef(false);
    const selectedAudience = useRef<number>(defaultAudience);
    const commentCount = useRef<number>(0);

    const [lastComment, setLastComment] = useState<string | null>(null);
    const [lastCommentDate, setLastCommentDate] = useState<string | null>(null);
    const [lastCommentUser, setLastCommentUser] = useState<string | null>(null);
    const [removed, setRemoved] = useState(false);
    //const [showAsTextBox, setShowAsTextBox] = useState
    const [visible, setVisible] = useState<boolean>(false);

    const commentsApiPath = stepId != null ? grantApi.getStepCommentsPath(grantId, stepId) : grantApi.getPhaseCommentsPath(grantId, phaseId);

    const commentsDataSource = new DataSource({
        store: createStore({
            key: 'id',
            loadUrl: sharedApi.generateURL(commentsApiPath),
            insertUrl: sharedApi.generateURL(commentsApiPath),
            updateUrl: sharedApi.generateURL(commentsApiPath),
            deleteUrl: sharedApi.generateURL(commentsApiPath),
            onBeforeSend: (method, ajaxOptions) => {
                ajaxOptions.xhrFields = { withCredentials: true };
                ajaxOptions.headers = { 'Authorization': 'Bearer ' + userApi.getAccessToken() };
                if (ajaxOptions.data.key != null) {
                    ajaxOptions.url += "/" + ajaxOptions.data.key + `?module=${module}`;
                }
                else {
                    // When the notify checkbox is hidden, the ref isn't mounted — fall back to false.
                    const notifyValue = notifyVisible ? notifyCheckRef.current?.instance?.option("value") : false;
                    ajaxOptions.url += `?notify=${notifyValue}&module=${module}`
                }
            },
            onLoaded: (e) => {
                if (e.length > 0) {
                    if (!parentStateHasBeenSet.current) {
                        setLastComment(e[0].comment);

                        var dateString = getFormattedDate(e[0].dateUpdated);

                        setLastCommentDate(dateString);

                        if (returnLastCommentDate) {
                            returnLastCommentDate(dateString);
                        }

                        setLastCommentUser(getUserFullName(e[0].updatedByUser));
                    }
                }
                else {
                    if (!parentStateHasBeenSet.current) {
                        setLastComment(null);

                        var emptyDateString = (removed) ? new Date().toISOString() : null;

                        setLastCommentDate(emptyDateString);

                        if (returnLastCommentDate) {
                            returnLastCommentDate(emptyDateString);
                        }

                        setLastCommentUser(null);
                    }
                }
                editingref.current = false;
                parentStateHasBeenSet.current = true;

                commentCount.current = e.length;
            },
            onRemoved: (e) => {
                setRemoved(true);
                parentStateHasBeenSet.current = false;
            }
        })
    });

    useEffect(() => {
        if (visible) {
            notifyCheckRef.current?.instance.option("value", notifyDefault);
            if (!app.UserIsPO && !app.UserIsORF && !app.UserIsSuper) {
                if (grid.current) {
                    interval.current = setInterval(() => {
                        if (!editingref.current) {
                            grid.current?.instance.repaint(); // repaint the grid every 10 seconds
                        }
                    }, 10000);
                }

                return () => clearInterval(interval.current as NodeJS.Timeout);
            }
        }
    }, [visible]);

    const dateRender = (data: any) => (
        <div>
            {
                data.data.dateUpdated ?
                    (
                        <div>{getFormattedDate(data.data.dateUpdated)}</div>
                    ) : <></>
            }
        </div>
    );

    const hidePhaseComments = (): boolean => {
        setVisible(false);
        return true;
    }

    const canEdit = (e: any) => {

        if (isReadOnly)
            return false;

        if (app.UserIsSuper || app.UserIsORF || app.UserIsPO) {
            return true;
        }
        else {
            return isWithinAllowableEditingTimeframe(e.row.data);
        }
    }

    const isWithinAllowableEditingTimeframe = (data: any) => {
        var dateString = data.dateCreated;
        var createdById = data.createdByUser?.id;
        const givenDate = new Date(dateString);
        const now = new Date();

        const timeframeMinutes = app.configuration.phaseCommentEditMinutes;

        const diffInMilliseconds = Math.abs(now.getTime() - givenDate.getTime());
        const timeframeMinutesInMilliseconds = timeframeMinutes * 60 * 1000;

        return (diffInMilliseconds <= timeframeMinutesInMilliseconds) && (createdById === app.userId);
    };

    function generateGUID() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
            var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    type DataGridChange = {
        data: any;
        key: any;
        type: "insert" | "update" | "remove";
    };

    const addComment = async (e: any) => {

        var isValid = validateTextBox();

        // notifyCheckRef may be null when the notify checkbox is hidden (notifyVisible=false) —
        // don't gate the save on it.
        if (grid.current != null &&
            newCommentTextbox.current != null &&
            newCommentTextValidationGroup.current != null) {
            if (isValid) {
                grid.current.instance.beginUpdate();
                var currentChanges = grid.current.instance.option("editing.changes") as DataGridChange[];
                var id = generateGUID();
                currentChanges.push({
                    data: { id: id, comment: newCommentTextbox.current.instance.option("value"), audience: selectedAudience.current },
                    key: id,
                    type: "insert"
                });
                grid.current.instance.option("editing.changes", currentChanges);
                grid.current.instance.endUpdate();
                await grid.current.instance.saveEditData();
                newCommentTextbox.current.instance.option("value", undefined)
                editingref.current = false;
                parentStateHasBeenSet.current = false;
                notifyCheckRef.current?.instance.option("value", notifyDefault);
                newCommentTextValidationGroup.current.instance.reset();

                if (onCommentAdded)
                    onCommentAdded();
            }
            else {
                newCommentTextbox.current.instance.option("value", undefined);
            }
        }
    }

    const refreshLatest = async (e: any) => {
        if (onCommentAdded)
            onCommentAdded();
    }

    const nameRender = (e: any) => {
        var user = e.data.createdByUser;
        return getUserFullName(user);
    }

    const validateTextBox = () => {
        var result = newCommentTextValidationGroup.current?.instance.validate();

        if (result?.isValid) {
            newCommentButton.current?.instance.option("disabled", false);
        }
        else {
            newCommentButton.current?.instance.option("disabled", true);
        }

        return result?.isValid;
    }

    const getFormattedDate = (date: any) => {
        return format(parseISO(date), 'MM/dd/yyyy h:mm a');
    }

    const phaseCommentsButtonOptions = {
        disabled: false,
        icon: "comment",
        text: "",
        onClick: (e: any) => {
            setVisible(true);
        },
    };

    const textAreaHeight = () => {
        if (showAudience) {
            return 135;
        }
        else {
            return 70;
        }
    }

    const selectAudience = (value: number) => {
        selectedAudience.current = value;
        audienceSelectBoxRef.current?.instance.option("value", value);
    }

    //TODO change displayType div to use devextreme Toolbar
    //TODO tooltip creation should be in a separate shared component
    return (
        <>
            {displayType === "button" && (
                <Button
                    disabled={isDisabled}
                    icon="comment"
                    stylingMode="contained"
                    text={`Comments (${commentCount.current})`}
                    onClick={(e: any) => {
                        setVisible(true);
                    }}>
                </Button>
            )}

            {displayType === "div" && (
                <h6 className="latestCommentHeader">
                    <span className="latestCommentHeaderText">Latest Comment</span>
                    <span className="latestCommentHeaderButton">
                        <Button
                            icon="comment"
                            onClick={(e: any) => {
                                setVisible(true);
                            }}>
                        </Button>
                    </span>
                </h6>
            )}

            <div id={`GrantComment${grantId}${phaseId}${stepId}Container`}>
                {displayType === "div" && (
                    <div className="latestCommentContent">{truncateWithEllipsis(lastComment, 300) ?? "No comments"}</div>
                )}
                {displayType === "textbox" && (
                    <>
                        <TextBox
                            className="grantDetails-computed"
                            value={lastComment ?? undefined}
                            id={`GrantComment${grantId}${phaseId}${stepId}`}
                            ref={commentTextBoxRef}
                            readOnly={true}
                        >
                            <TextBoxButton name="PhaseComments" options={phaseCommentsButtonOptions} ></TextBoxButton>
                        </TextBox>
                    </>
                )}
            </div>
            <GrantDetailCommentTooltip
                commentText={lastComment}
                commentUser={lastCommentUser}
                commentDate={lastCommentDate}
                target={`GrantComment${grantId}${phaseId}${stepId}Container`} />
            <Popup
                height={500}
                hideOnOutsideClick={hidePhaseComments}
                onHiding={hidePhaseComments}
                title={`${name} Comments`}
                visible={visible}
                width={750}
            >
                {!isReadOnly ? (
                    <div className="dx-field col-12" style={{ padding: "0 0 20px 10px" }}>
                        <div className="row">
                            <div className="dx-field col-8">
                                <ValidationGroup ref={newCommentTextValidationGroup}>
                                    <TextArea
                                        disabled={isReadOnly}
                                        height={textAreaHeight()}
                                        maxLength={2000}
                                        onValueChanged={validateTextBox}
                                        placeholder="enter your new comment here (max characters: 2000)"
                                        ref={newCommentTextbox}
                                        width="95%"
                                    >
                                        <Validator>
                                            <RequiredRule></RequiredRule>
                                            <StringLengthRule max={2000} message="Max length for a comment is 2000 characters"></StringLengthRule>
                                        </Validator>
                                    </TextArea>
                                </ValidationGroup>
                            </div>
                            <div className="dx-field col-4">
                                <div className="col-12">
                                    {notifyVisible && (
                                        <div className="row">
                                            <div className="dx-field col-11">
                                                <CheckBox
                                                    defaultValue={true}
                                                    disabled={isReadOnly}
                                                    text="Notify all participants via email"
                                                    ref={notifyCheckRef}
                                                ></CheckBox>
                                            </div>
                                        </div>
                                    )}
                                    {showAudience && (
                                        <div className="row">
                                            <div className="dx-field col-11" style={{ paddingBottom: "20px" }}>
                                                {/* Read-only, not disabled - a disabled box greys out the value it is there to show. */}
                                                <div id={audienceTooltipTargetId} className="comment-audience" data-testid="comment-audience">
                                                    <SelectBox
                                                        dataSource={audienceOptions}
                                                        readOnly={true}
                                                        displayExpr="text"
                                                        label="Comment Audience"
                                                        onValueChanged={(e) => selectAudience(e.value)}
                                                        ref={audienceSelectBoxRef}
                                                        defaultValue={defaultAudience}
                                                        valueExpr="value"
                                                    ></SelectBox>
                                                    <i className="dx-icon-info comment-audience-info" aria-hidden="true"></i>
                                                </div>
                                                <Tooltip
                                                    target={`#${audienceTooltipTargetId}`}
                                                    showEvent="mouseenter"
                                                    hideEvent="mouseleave"
                                                    hideOnOutsideClick={false}
                                                    position="top"
                                                >
                                                    <div className="comment-audience-tooltip">
                                                        <div>Only these roles can see comments added here:</div>
                                                        <ul>
                                                            {audienceRoleNames.map(name => <li key={name}>{name}</li>)}
                                                        </ul>
                                                        <div>The audience follows your role and cannot be changed.</div>
                                                    </div>
                                                </Tooltip>
                                            </div>
                                        </div>
                                    )}
                                    <div className="row">
                                        <div className="dx-field col-11">
                                            <Button
                                                disabled={isReadOnly}
                                                text="Add Comment"
                                                onClick={async (e) => await addComment(e)}
                                                ref={newCommentButton}
                                            ></Button>
                                        </div>
                                    </div>
                                </div>

                            </div>
                        </div>
                    </div>
                ) : (<></>)}
                <DataGrid
                    dataSource={commentsDataSource}
                    height="75%"
                    onEditingStart={() => { editingref.current = true; parentStateHasBeenSet.current = false; }}
                    onSaved={async (e) => { await refreshLatest(e); editingref.current = false; }}
                    onEditCanceled={() => { editingref.current = false; parentStateHasBeenSet.current = true; }}
                    repaintChangesOnly={true}
                    rowAlternationEnabled={true}
                    showBorders={false}
                    showColumnLines={false}
                    showRowLines={true}
                    wordWrapEnabled={true}
                    ref={grid}
                >
                    <Editing
                        mode="row"
                        allowAdding={false}
                        allowDeleting={canEdit}
                        allowUpdating={canEdit}
                        confirmDelete={true}
                        useIcons={true}
                    >
                    </Editing>
                    <Paging defaultPageSize={100} />
                    <RemoteOperations
                        filtering={true}
                        paging={true}
                        sorting={true}
                        summary={true}
                        grouping={true}
                        groupPaging={true}
                    />
                    <Scrolling mode="virtual"></Scrolling>
                    <Column
                        allowEditing={canEdit}
                        allowSorting={false}
                        dataField="comment"
                        dataType="string"
                        editorOptions={{ maxLength: 2000 }}
                    >
                        <RequiredRule></RequiredRule>
                        <StringLengthRule max={2000} message="Max length for a comment is 2000 characters"></StringLengthRule>
                    </Column>
                    <Column
                        allowEditing={false}
                        allowSorting={false}
                        caption="User"
                        cellRender={nameRender}
                        dataField="updatedByUser"
                        dataType="string"
                        editCellRender={nameRender}
                        width="150px"
                    ></Column>
                    <Column
                        allowEditing={false}
                        allowSorting={false}
                        caption="Date"
                        cellRender={dateRender}
                        dataField="dateUpdated"
                        dataType="string"
                        sortOrder="desc"
                        width="150px"
                    ></Column>
                </DataGrid>
            </Popup>
        </>
    )
});